import React, { useEffect, useState, MouseEvent } from 'react';
import logger from '@gral/log';

import Router from './Router';
import * as Sentry from '@sentry/react';

import detectLanguage from '@utils/i18n/languageDetector';
import actStore from '@flux/actions/actStore';

import { IAppProps, ISessionBadge, ELoginState } from './interfaces/App.interface';
import { routerActions } from '@redux/slices/states/router/routerSlice';

import Body from '@modules/Body';
import { Loading } from '@components/Misc';

//REDUX
import { useAppDispatch, useAppSelector } from '@redux/store';
import {
  getEventsTypes,
  getSettings,
  getACL,
  loginWithToken,
  getReadingTypes,
  getUnits,
  getIndicators,
  logout,
} from '@services/ApiService';
import TLoading from '@components/Loading';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import QuickAccessModal from '@modules/tableV3/QuickAccessModal';
import TNotifs from '@components/TNotifs';
import GApp from '@gral/app';
import useJWTChecker from '@utils/hooks/useJWTChecker';
import { useRouterContext } from '@utils/context/RouterContext';
import { Box } from '@mui/material';
import { NavTopBar, Sidebar } from '@ts/storybook';
import { IMenuItem } from '@ts/ui-models';
import { useTranslation } from 'react-i18next';
import { Logo } from '@components/Outputs';

const log = logger(`APP`);
const App = (props: IAppProps) => {
  const dispatch = useAppDispatch();

  const { navigate, location, isMobile } = useRouterContext();
  useJWTChecker();

  const { transferPropsRoute } = useRouterContext();
  const { isEmbededMobile } = transferPropsRoute;
  const sessionBadge = useAppSelector(state => state.global.sessionBadge);
  const loginState = useAppSelector(state => state.global.loginState);
  const user = useAppSelector(state => state.global.user);
  const listMenu = useAppSelector(state => state.global.settings?.menu);

  const [firstFetchDone, setFirstFetchDone] = useState(false);

  const {
    t,
    i18n: { language: currentLanguage, changeLanguage },
  } = useTranslation();

  //#region Functions
  const setSentryUser = (sessionBadge: ISessionBadge) => {
    Sentry.setUser(sessionBadge);
  };

  const languageMap = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    ar: 'عربي',
  };

  const getLanguageItems = () => {
    if (!languageMap) return;

    return (Object.keys(languageMap) as Array<keyof typeof languageMap>).map(lng => ({
      id: 'navigation.languages.' + lng,
      caption: languageMap[lng],
      onClick: () => changeLanguage(lng),
    }));
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, menuItem: IMenuItem) => {
    const { route, id } = menuItem;

    if (!route) return;

    const queryParams = `?filterID=${id}`;

    if (!(event.ctrlKey || event.metaKey)) {
      event.preventDefault();

      // Clean history and lastTabClicked
      localStorage.removeItem('lastTabClicked');
      dispatch(routerActions.clearHistoryRoutes());
      navigate(route + queryParams, { state: { menuOption: menuItem, resetHistoryLevel: 0 } });
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      window.open(route + queryParams, '_blank');
      event.preventDefault();
      return;
    }

    // if (isMobile) {
    //   setDefaultOpened(false);
    // }

    return;
  };
  //#endregion

  //#region Render
  const renderApp = () => {
    let screen;
    switch (loginState) {
      case ELoginState.LOADING_USER_PARAMS:
      case ELoginState.LOGGED_OUT:
        screen = (
          <>
            <TLoading />
            <Router {...props} />
          </>
        );
        break;
      //case ELoginState.LOGGING_IN:
      case ELoginState.LOGGED_IN:
        screen = (
          <>
            {/*  <BrowserNotification /> */}
            <TLoading />
            <QuickAccessModal {...props} />
            {!isEmbededMobile ? (
              <Sidebar
                {...props}
                isMobile={isMobile!}
                body={<Body {...props} />}
                onItemMenuClick={handleClick}
                listMenu={listMenu?.menu}
                navBar={
                  <NavTopBar
                    {...props}
                    customLogo={<Logo />}
                    user={user!}
                    menuItems={[
                      {
                        id: 'navigation.my-user',
                        icon: 'person',
                        caption: 'My user',
                        onClick: () => {
                          navigate(`/user/${user?.id}/view`, { state: { resetHistoryLevel: 0 } });
                        },
                      },
                      {
                        id: 'navigation.my-roles',
                        caption: 'My roles',
                        icon: 'badge',
                        onClick: () => {
                          localStorage.removeItem('lastTabClickedconfig');
                          navigate('/config/access/myRoles', { state: { resetHistoryLevel: 0 } });
                        },
                      },
                      {
                        id: 'navigation.configurations',
                        caption: 'Configuration',
                        icon: 'settings',
                        onClick: () => {
                          localStorage.removeItem('lastTabClickedconfig');
                          navigate('/config', { state: { resetHistoryLevel: 0 } });
                        },
                      },
                      {
                        id: 'navigation.languages',
                        icon: 'language',
                        caption: ['Language', '(', currentLanguage, ')'],
                        subMenuItems: getLanguageItems(),
                      },
                      {
                        id: 'navigation.logout',
                        icon: 'exitToApp',
                        caption: 'Log out',
                        onClick: () => {
                          logout(dispatch);
                        },
                      },
                    ]}
                  />
                }
              />
            ) : (
              <Box sx={{ height: '100vh' }}>
                <Body {...props} />
              </Box>
            )}
          </>
        );

        break;
      default:
        screen = <Loading withoutHeight />;
    }
    return (
      <>
        <TNotifs id="global-notif" notif={props.app.get('notif')} />
        {firstFetchDone && screen}
      </>
    );
  };
  //#endregion

  //#region Effects
  const useEffectChangeStateLoginLoadStaticInfo = (deps: React.DependencyList) => {
    useEffect(() => {
      //const _loginState = props.app.getIn(['cx', 'loginState']);

      /* if (_loginState !== state.loginState) {
        setState({ loginState: _loginState });
      } */

      switch (loginState) {
        case ELoginState.LOGGED_OUT:
          if (location.pathname !== '/sso/microsoft/callback' && location.pathname !== '/forgot_password') {
            navigate('/login');
          }
          break;
        /* case ELoginState.LOGGING_IN:
          navigate('/');
          break; */
        case ELoginState.LOGGED_IN:
          if (location.pathname === '/sso/microsoft/callback') {
            navigate('/');
          }
          break;
        case ELoginState.LOADING_USER_PARAMS: {
          const fetchDataPromises = [getEventsTypes(dispatch)];
          //const fetchDataPromises = [getItemsTypes(dispatch), getEventsTypes(dispatch)];
          Promise.all(fetchDataPromises);
          break;
        }
        default:
          break;
      }
    }, deps);
  };

  const useEffectChangeRoleLoadSettings = (deps: React.DependencyList) => {
    useEffect(() => {
      switch (loginState) {
        case ELoginState.LOGGED_OUT:
          Sentry.setUser(null);
          setFirstFetchDone(true);
          break;
        case ELoginState.LOGGED_IN:
          if (sessionBadge === null) {
            dispatch(globalActions.logout());
            navigate('/login');
          }
          break;
        case ELoginState.LOADING_USER_PARAMS:
          if (sessionBadge) {
            //if (sessionBadge.roleId !== sessionBadge.roleId) dispatch(globalActions.updateRole({ ...sessionBadge }));

            setSentryUser(sessionBadge);
            const fetchDataPromises = [
              getSettings(dispatch, sessionBadge),
              getACL(dispatch, sessionBadge),
              getReadingTypes(dispatch),
              getUnits(dispatch),
              getIndicators(dispatch),
            ];

            Promise.all(fetchDataPromises)
              .then(v => {
                const [settingsResp, aclsResp] = v;

                if (!settingsResp?.payload?.data || !aclsResp?.payload?.data) {
                  dispatch(globalActions.logout());
                  return;
                }

                const settings = settingsResp.payload.data;
                const userAcls = aclsResp.payload.data;

                GApp.cxStore._setSettingsACLsSuccess(settings, userAcls);
                const lng = detectLanguage();
                actStore.changeLng(lng);
                setFirstFetchDone(true);
              })
              .catch(() => {
                dispatch(globalActions.logout());
              });
          }
          break;
        default:
          break;
      }
    }, deps);
  };
  //#endregion

  useEffect(() => {
    const token = GApp.cxStore.getToken();

    switch (loginState) {
      case ELoginState.LOGGED_OUT:
        if (!token) {
          log.info('Detecting language...');
          const lng = detectLanguage();
          actStore.changeLng(lng);
        }
        break;
      case ELoginState.LOGGING_IN:
        log.info('Logging in automatically... from token');
        loginWithToken(dispatch);
        break;
      default:
        break;
    }
  }, []);
  useEffectChangeRoleLoadSettings([sessionBadge, loginState]);
  useEffectChangeStateLoginLoadStaticInfo([loginState]);

  return renderApp();
};

export default App;
